import { PaymentMethod } from '@synergy/models';
import { createSelector } from 'reselect';
import { errorsToTouch, nullIfEmpty } from '@synergy/utils';

import { AppState } from '../../../../../../store/models/app-state';
import { CustomerConditions } from '../models/customer-conditions';
import { CustomerConditionsFormValues } from '../models/customer-conditions-form-values';
import { CustomerConditionsPatchRequest } from '../api/models/customer-conditions-patch-request';

const getCustomerDetail = (state: AppState) => state.contactsAndOrganizations.customers.customerDetail;

export const customerConditionSelector = createSelector(getCustomerDetail, state => state.conditions);

/**
 * The defaults the mask fills in for values the server left empty. Shared with
 * the unsaved-changes baseline (#2159), or every default would read as input.
 */
const withDefaults = (conditions?: CustomerConditions | null): CustomerConditionsFormValues => ({
    base: conditions?.base ?? 0,
    bulk: conditions?.bulk ?? 0,
    currency: conditions?.currency ?? 'EUR',
    discount: conditions?.discount ?? 0,
    internalNote: conditions?.internalNote ?? '',
    taxRateId: conditions?.taxRateId ?? null,
    paymentTermId: conditions?.paymentTermId ?? null,
    paymentMethod: conditions?.paymentMethod ?? PaymentMethod.BankTransfer,
    memberSince: conditions?.memberSince ?? null,
    isMember: conditions?.isMember ?? false,
    hasBonusPayout: conditions?.hasBonusPayout ?? false,
    bonusBase: conditions?.bonusBase ?? 70,
    bonusPayout: conditions?.bonusPayout ?? null,
    ledgerAccountId: conditions?.ledgerAccountId ?? null,
});

export const customerDetailConditionsFormDataSelector = createSelector(customerConditionSelector, state => ({
    formValues: withDefaults(state.conditions),
    errors: state.errors,
    touched: errorsToTouch(state.errors),
}));

/** What the tab compares against to decide whether anything is unsaved. */
export const customerDetailConditionsBaselineSelector = createSelector(customerConditionSelector, state =>
    state.baseline ? withDefaults(state.baseline) : undefined
);

export const customerConditionsRequestSelector = createSelector(customerConditionSelector, state => {
    if (!state.conditions) {
        return;
    }

    const result: CustomerConditionsPatchRequest = {
        base: state.conditions.base ?? 0,
        bulk: state.conditions.bulk ?? 0,
        currency: state.conditions.currency ?? 'EUR',
        discount: state.conditions.discount ? state.conditions.discount : 0,
        internalNote: nullIfEmpty(state.conditions.internalNote),
        taxRateId: state.conditions.taxRateId ?? 0,
        paymentTermId: state.conditions.paymentTermId ?? 0,
        paymentMethod: state.conditions.paymentMethod ?? PaymentMethod.BankTransfer,
        memberSince: state.conditions.memberSince ?? null,
        isMember: state.conditions.isMember,
        hasBonusPayout: state.conditions.hasBonusPayout,
        bonusPayout: state.conditions.bonusPayout ?? null,
        bonusBase: state.conditions.bonusBase,
        ledgerAccountId: state.conditions.ledgerAccountId ?? null,
    };
    return result;
});
